import React, { useState, useEffect } from 'react';
import { Calendar, Check, Star, TrendingUp, Heart, Target, MessageCircle, Award, Loader2 } from 'lucide-react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { apiClient } from '../api/client';
import { useAuth } from '../contexts/AuthContext';

const WeeklyReport = () => {
  const { user } = useAuth();
  const [recap, setRecap] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiClient.request('/api/enhancements/weekly-recap')
      .then(setRecap)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="p-8 flex items-center justify-center min-h-[400px]">
          <Loader2 size={32} className="animate-spin text-purple-500" />
        </div>
      </DashboardLayout>
    );
  }

  const moodPercent = recap?.avg_mood ? Math.round(recap.avg_mood * 100) : null;
  const getMoodColor = (pct) => {
    if (pct >= 80) return 'text-emerald-600';
    if (pct >= 60) return 'text-yellow-600';
    return 'text-orange-600';
  };

  return (
    <DashboardLayout>
      <div className="p-4 sm:p-8 max-w-3xl mx-auto" data-testid="weekly-report-page">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-purple-100 text-purple-700 rounded-full text-sm font-semibold mb-3">
            <Calendar size={16} /> Week Ending {recap?.week_ending || '--'}
          </div>
          <h1 className="text-3xl font-bold text-gray-900">Your Weekly Wins</h1>
          <p className="text-gray-600 mt-1">Hey {recap?.name || user?.name || 'there'}, here&apos;s how your week went.</p>
        </div>

        {/* Highlights */}
        {recap?.highlights?.length > 0 && (
          <div className="mb-6 p-5 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl">
            <h3 className="font-bold text-amber-900 mb-3 flex items-center gap-2"><Star size={18} className="text-amber-500" /> Highlights</h3>
            <ul className="space-y-2">
              {recap.highlights.map((h, i) => (
                <li key={i} className="flex items-center gap-2 text-sm text-amber-800">
                  <Check size={16} className="text-amber-600 flex-shrink-0" /> {h}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="p-4 bg-white rounded-xl border border-gray-200 text-center shadow-sm" data-testid="recap-checkins">
            <div className="flex justify-center mb-2"><TrendingUp size={24} className="text-blue-500" /></div>
            <p className="text-3xl font-bold text-gray-900">{recap?.checkins || 0}</p>
            <p className="text-xs text-gray-500 mt-1">Check-ins</p>
          </div>
          <div className="p-4 bg-white rounded-xl border border-gray-200 text-center shadow-sm" data-testid="recap-challenges">
            <div className="flex justify-center mb-2"><Target size={24} className="text-purple-500" /></div>
            <p className="text-3xl font-bold text-gray-900">{recap?.challenges || 0}</p>
            <p className="text-xs text-gray-500 mt-1">Challenges</p>
          </div>
          <div className="p-4 bg-white rounded-xl border border-gray-200 text-center shadow-sm" data-testid="recap-convos">
            <div className="flex justify-center mb-2"><MessageCircle size={24} className="text-green-500" /></div>
            <p className="text-3xl font-bold text-gray-900">{recap?.conversations || 0}</p>
            <p className="text-xs text-gray-500 mt-1">Conversations</p>
          </div>
          <div className="p-4 bg-white rounded-xl border border-gray-200 text-center shadow-sm" data-testid="recap-mood">
            <div className="flex justify-center mb-2"><Heart size={24} className="text-pink-500" /></div>
            <p className={`text-3xl font-bold ${moodPercent ? getMoodColor(moodPercent) : 'text-gray-400'}`}>
              {moodPercent ? `${moodPercent}%` : '--'}
            </p>
            <p className="text-xs text-gray-500 mt-1">Avg Mood</p>
          </div>
        </div>

        {/* Streak & Badges */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
          <div className="p-5 bg-white rounded-xl border border-gray-200 shadow-sm flex items-center gap-4" data-testid="recap-streak">
            <div className="p-3 bg-orange-100 rounded-xl">
              <TrendingUp size={24} className="text-orange-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Current Streak</p>
              <p className="text-2xl font-bold text-gray-900">{recap?.current_streak || 0} days</p>
            </div>
          </div>
          <div className="p-5 bg-white rounded-xl border border-gray-200 shadow-sm flex items-center gap-4" data-testid="recap-badges">
            <div className="p-3 bg-purple-100 rounded-xl">
              <Award size={24} className="text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Badges This Week</p>
              <p className="text-2xl font-bold text-gray-900">{recap?.badges_earned || 0}</p>
            </div>
          </div>
        </div>

        {/* Hearts Balance */}
        <div className="p-5 bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-xl text-center" data-testid="recap-hearts">
          <Heart size={24} className="mx-auto text-pink-500 mb-1" fill="currentColor" />
          <p className="text-sm text-gray-500">Hearts Balance</p>
          <p className="text-3xl font-bold text-gray-900">{(recap?.hearts_balance || 0).toLocaleString()}</p>
        </div>

        <p className="text-center text-gray-400 text-sm mt-6">
          Weekly recaps are generated every Sunday and emailed to you.
        </p>
      </div>
    </DashboardLayout>
  );
};

export default WeeklyReport;
